/**
 * NativeBridge — pont JS léger Web ↔ shell natif (app mobile marque blanche).
 *
 * Pendant côté web du AppBridge natif (unisoft_mobile/src/AppBridge.js). Même
 * protocole que l'AppService admin (postMessage {type, data, session?}).
 *
 * SENS WEB → NATIF : fire-and-forget (post). On n'a pas (encore) d'ACK promisifié
 * complet — les primitives actuelles (haptique, ouverture système, push) n'ont pas
 * besoin d'une valeur de retour synchrone.
 *
 * SENS NATIF → WEB : le natif POUSSE des events (ex. 'push.token' avec le jeton de
 * notification). On les capte ici via un mini-dispatcher (window + document, comme
 * l'admin) et on notifie les abonnés (subscribe). Le natif initie : c'est lui qui
 * détient le jeton FCM et le remonte.
 *
 * INVARIANT NAVIGATEUR : hors app (pas de window.ISAPP / pas de ReactNativeWebView),
 * TOUTES les méthodes sont des no-op silencieux et aucun listener n'est posé. Zéro
 * impact sur le trafic web classique. SSR-safe : tout accès à window est gardé.
 *
 * window.ISAPP est posé par le shell natif AVANT le 1er load (INJECTED_JS).
 * window.APP_MOBILE_CONFIG_ID porte l'id de l'app marque blanche (pour l'enregistrement
 * du jeton push).
 */

// Détection de présence app. SSR-safe.
function isApp() {
    if (typeof window === "undefined") return false;
    return window.ISAPP === true || !!window.ReactNativeWebView;
}

// id de la mobile_config (= l'app marque blanche), injecté par le natif. "" hors app.
function getMobileConfigId() {
    if (typeof window === "undefined") return "";
    return typeof window.APP_MOBILE_CONFIG_ID === "string" ? window.APP_MOBILE_CONFIG_ID : "";
}

// Plateforme native ("ios"|"android"), injectée par le natif. "" hors app.
function getPlatform() {
    if (typeof window === "undefined") return "";
    return typeof window.APP_PLATFORM === "string" ? window.APP_PLATFORM : "";
}

// Envoi brut vers le natif (fire-and-forget). Renvoie false si pas en app.
function postNative(type, data) {
    if (typeof window === "undefined") return false;
    const rn = window.ReactNativeWebView;
    if (!rn || typeof rn.postMessage !== "function") return false;
    try {
        rn.postMessage(JSON.stringify({ type, data }));
        return true;
    } catch (_) {
        return false;
    }
}

// --- Réception des events natifs (natif → web) --------------------------------
// Le natif envoie des messages {type, data} via webview.postMessage → ils arrivent
// comme un event "message" sur document (iOS/Android) et/ou window. On parse et on
// dispatch aux abonnés par type. Un seul couple de listeners global, posé une fois.
const _subscribers = new Map(); // type -> Set<cb>
let _listenerInstalled = false;

function _dispatch(type, data) {
    const set = _subscribers.get(type);
    if (!set) return;
    for (const cb of set) {
        try { cb(data); } catch (_) {}
    }
}

function _onRawMessage(event) {
    // event.data peut être l'objet déjà parsé (rare) ou une string JSON.
    let payload = event && event.data;
    if (typeof payload === "string") {
        try { payload = JSON.parse(payload); } catch (_) { return; }
    }
    if (!payload || typeof payload !== "object" || typeof payload.type !== "string") return;
    _dispatch(payload.type, payload.data);
}

function _ensureListener() {
    if (_listenerInstalled || typeof window === "undefined") return;
    // window ET document : selon la plateforme RN WebView, l'un ou l'autre reçoit.
    try { window.addEventListener("message", _onRawMessage); } catch (_) {}
    try { document.addEventListener("message", _onRawMessage); } catch (_) {}
    _listenerInstalled = true;
}

/**
 * S'abonne à un type d'event natif. Renvoie une fonction de désabonnement.
 * No-op hors app (le listener n'est même pas posé). Le 1er abonnement installe les
 * listeners globaux.
 */
function subscribe(type, cb) {
    if (!isApp() || typeof cb !== "function") return () => {};
    _ensureListener();
    let set = _subscribers.get(type);
    if (!set) { set = new Set(); _subscribers.set(type, set); }
    set.add(cb);
    return () => {
        const s = _subscribers.get(type);
        if (s) s.delete(cb);
    };
}

// --- Haptique -----------------------------------------------------------------
const HAPTIC_TYPES = new Set([
    "light", "medium", "heavy", "rigid", "soft",
    "selection", "success", "warning", "error",
]);

let _lastHapticAt = 0;
const HAPTIC_THROTTLE_MS = 40;

/**
 * Déclenche un retour haptique natif. No-op hors app. Fire-and-forget.
 * @param {string} type un des HAPTIC_TYPES (défaut "light")
 */
function haptic(type = "light") {
    if (!isApp()) return false;
    const t = HAPTIC_TYPES.has(type) ? type : "light";
    const now = (typeof performance !== "undefined" && performance.now)
        ? performance.now()
        : new Date().getTime();
    if (now - _lastHapticAt < HAPTIC_THROTTLE_MS) return false;
    _lastHapticAt = now;
    return postNative("haptic", { type: t });
}

// --- Notifications push -------------------------------------------------------
// Demande au natif de déclencher la permission OS + la récupération du jeton. Le
// natif répond en POUSSANT l'event 'push.token' (capté via subscribe). No-op hors
// app. Fire-and-forget.
function requestPush() {
    if (!isApp()) return false;
    return postNative("push.requestPermission", {});
}

// Redemande le jeton courant sans re-prompter (le natif renvoie 'push.token').
function refreshPushToken() {
    if (!isApp()) return false;
    return postNative("push.getToken", {});
}

const NativeBridge = {
    isApp,
    getMobileConfigId,
    getPlatform,
    haptic,
    subscribe,
    requestPush,
    refreshPushToken,
    // Échappatoire générique si on doit pousser un autre event natif fire-and-forget.
    post: postNative,
};

export default NativeBridge;
export { isApp, haptic, subscribe, requestPush, refreshPushToken, getMobileConfigId, getPlatform, postNative as post };
